import type { Dimension, MapData, Topic } from "@serendipityai/tm-visual";
import { EDIT_MODE, NO_LOGO, TransformationMap as TopicVisualization, VIEW_MODE } from "@serendipityai/tm-visual";
import debounce from "lodash.debounce";
import type { DragEvent } from "react";
import { memo, useCallback, useEffect, useRef } from "react";
import { css, styled } from "styled-components";

import { mediaStyles } from "common/styles";
import { useTranslate } from "common/utilities/useTranslate";
import { useTheme } from "domains/theme/useTheme";
import { LoadingIndicator } from "domains/transformationMap/components/MapLoadingIndicator";

const StyledContainer = styled.div`
  box-sizing: border-box;
  height: calc(100% - 2rem);
  width: 100%;
  ${mediaStyles(({ spacing }) => `padding: ${spacing}rem`, { small: { spacing: 1 }, extraLarge: { spacing: 2 } })};
`;

const StyledSvg = styled.svg`
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;

  ${({ theme }) => css`
    .transformation-map {
      .remove-cross {
        fill: ${theme.colors.fillPrimary};
      }
    }
  `}
`;

const SkipTransformationMap = styled.a`
  height: fit-content;
  opacity: 0;
  padding: 2px;
  position: absolute;
  width: fit-content;
  z-index: -1;

  &:focus-visible {
    opacity: 1;
    position: relative;
  }
`;

type TransformationMapProps = {
  handleDragDrop?: (keyIssueId: string) => void;
  handleRemoveInnerNode: (topic: Topic) => void;
  handleSelectedOuterNode: (topic: Topic) => void;
  handleSelectImage: () => void;
  handleSelectInnerNode: (topic: Topic) => void;
  handleDeselectInnerNode: (topic: Topic) => void;
  image?: string;
  isDraggingTopic?: boolean;
  isEditing?: boolean;
  isLoading?: boolean;
  dimensions?: Dimension[];
  selectedInnerNode?: Topic;
  selectedOuterNode?: Topic;
  topic?: MapData;
};

// A drag over event is required on the drop zone
const handleDragOver = (event: DragEvent<SVGSVGElement>) => {
  event.preventDefault();
};

const dimensionToString = (dimension: Dimension) => {
  const { id, name, topics } = dimension;
  const topicNames = topics.map(({ id, name }) => `${id}-${name}`).join("_");
  return `${id}-${name}-${topicNames}`;
};

const mapToString = (dimensionList: Dimension[]) =>
  dimensionList?.map((dimension) => dimensionToString(dimension)).join("_");

export const TransformationMap = memo(
  ({
    handleDragDrop,
    handleRemoveInnerNode,
    handleSelectedOuterNode,
    handleSelectImage,
    handleSelectInnerNode,
    handleDeselectInnerNode,
    image,
    isDraggingTopic = false,
    isEditing = false,
    isLoading = false,
    dimensions: keyIssues,
    selectedInnerNode,
    selectedOuterNode,
    topic = {} as MapData,
  }: TransformationMapProps) => {
    const svgElement = useRef<SVGSVGElement>(null);
    const map = useRef<TopicVisualization | undefined>(new TopicVisualization());
    const translate = useTranslate();
    const selectedTheme = useTheme();

    useEffect(() => {
      if (!map.current) {
        return;
      }
      map.current.isDarkModeTheme(selectedTheme === "dark");
      requestAnimationFrame(() => {
        if (map.current) void map.current.update();
      });
    }, [selectedTheme]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedUpdateMap = useCallback(
      debounce(() => {
        requestAnimationFrame(() => {
          if (map.current) void map.current.update();
        });
      }, 500),
      [],
    );

    useEffect(() => {
      if (!svgElement.current || !map.current) {
        return;
      }

      map.current
        .data(topic)
        .mode(isEditing ? EDIT_MODE : VIEW_MODE)
        .isDarkModeTheme(selectedTheme === "dark")
        .copyrightLocation(NO_LOGO)
        .onSelectKeyIssue(handleSelectInnerNode)
        .onDeselectKeyIssue(handleDeselectInnerNode)
        .onSelectRemoveKeyIssue(handleRemoveInnerNode)
        .animationDuration(750)
        .copyrightText(null);

      const customMapMentionText = translate("app.topic.custom_map");
      if (customMapMentionText) {
        map.current.customMapMentionText(translate("app.topic.custom_map"));
      }

      void map.current.initialise(svgElement.current);

      window.addEventListener("resize", debouncedUpdateMap);
      const resizeObserver = new ResizeObserver(debouncedUpdateMap);
      resizeObserver.observe(svgElement.current);

      const svg = svgElement.current;

      return () => {
        map.current = undefined;
        if (svg) {
          svg.innerHTML = "";
          if (resizeObserver) {
            resizeObserver.unobserve(svg);
          }
        }
        window.removeEventListener("resize", debouncedUpdateMap);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const customMapMentionText = translate("app.topic.custom_map");
      if (!map.current || !customMapMentionText) return;
      void map.current.customMapMentionText(customMapMentionText).update();
    }, [translate]);

    useEffect(() => {
      if (!map.current) {
        return;
      }
      map.current.highlight(selectedInnerNode ?? topic);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInnerNode?.id]);

    useEffect(() => {
      if (!map.current) {
        return;
      }
      map.current.highlightEvidence(selectedOuterNode?.id);
    }, [selectedOuterNode?.id]);

    useEffect(() => {
      if (!map.current) {
        return;
      }
      map.current.onSelectInsightArea(handleSelectedOuterNode);
      requestAnimationFrame(() => {
        if (map.current) void map.current.update();
      });
    }, [handleSelectedOuterNode]);

    const list = keyIssues !== undefined && isEditing ? keyIssues : topic.dimensions;
    const dimensionsString = mapToString(list);

    useEffect(() => {
      if (!map.current) {
        return;
      }
      let newData: Partial<MapData> = {
        ...topic,
        dimensions: list,
      };
      if (image) {
        newData = { ...newData, image_url: image };
      }
      const newTopicData = { ...topic, ...newData };
      map.current.data(newTopicData).highlight(selectedInnerNode ?? topic, true);
      requestAnimationFrame(() => {
        if (map.current) void map.current.update();
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topic?.id, topic?.name, dimensionsString, image]);

    useEffect(() => {
      if (!map.current) {
        return;
      }
      if (isEditing) {
        map.current.mode(EDIT_MODE).onSelectImage(handleSelectImage).onSelectInsightArea(null);
      } else {
        map.current
          .mode(VIEW_MODE)
          .onSelectInsightArea(handleSelectedOuterNode)
          .onSelectImage(handleSelectImage)
          .highlight(selectedInnerNode ?? topic, true);
      }
      requestAnimationFrame(() => {
        if (map.current) void map.current.update();
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topic?.id, isEditing]);

    useEffect(() => {
      if (!map.current || !isEditing) return;
      void map.current.isDragging(isDraggingTopic).update();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDraggingTopic]);

    const handleDrop = (event: DragEvent<SVGSVGElement>) => {
      event.preventDefault();
      const keyIssueId = event.dataTransfer.getData("topicId");
      if (handleDragDrop) {
        handleDragDrop(keyIssueId);
      }
    };

    return (
      <StyledContainer>
        <SkipTransformationMap href="#topic-content">
          {translate("app.topic.skip_to_topic_content")}
        </SkipTransformationMap>
        <StyledSvg onDragOver={handleDragOver} onDrop={handleDrop} ref={svgElement} />
        {isLoading && <LoadingIndicator />}
      </StyledContainer>
    );
  },
);

TransformationMap.displayName = "TransformationMap";
